import { useState, useCallback, useRef } from 'react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { useDiscardGuard } from '@/components/ui/form-dialog-content'
import { Button } from '@/components/ui/button'
import { ServicioForm } from './ServicioForm'
import type { ServicioFormValues } from './forms'
import type { Servicio } from '@/types/database'

interface ServicioFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  servicio?: Servicio | null
  onSubmit: (data: ServicioFormValues) => void
  isLoading?: boolean
}

export function ServicioFormDialog({
  open,
  onOpenChange,
  servicio,
  onSubmit,
  isLoading,
}: ServicioFormDialogProps) {
  const [isFormDirty, setIsFormDirty] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)

  const isEditing = !!servicio

  const { holdClose, confirmation } = useDiscardGuard({
    isDirty: isFormDirty,
    onDiscard: () => onOpenChange(false),
  })

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && holdClose()) return
    onOpenChange(newOpen)
  }

  // Trigger submit desde el boton externo
  const handleExternalSubmit = useCallback(() => {
    formRef.current?.requestSubmit()
  }, [])

  return (
    <>
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0">
          <SheetHeader className="px-6 pt-6">
            <SheetTitle>
              {isEditing ? 'Editar Servicio' : 'Nuevo Servicio'}
            </SheetTitle>
            <SheetDescription>
              {isEditing
                ? 'Modifica los datos del servicio contable'
                : 'Agrega un nuevo servicio que ofreces a tus clientes'}
            </SheetDescription>
          </SheetHeader>

          {/* Contenido scrolleable */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <ServicioForm
              ref={formRef}
              servicio={servicio}
              onSubmit={onSubmit}
              onDirtyChange={setIsFormDirty}
              isLoading={isLoading}
              hideButtons
            />
          </div>

          {/* Footer sticky con botones */}
          <SheetFooter className="px-6 py-4 border-t bg-background">
            <div className="flex justify-end gap-3 w-full">
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={handleExternalSubmit}
                disabled={isLoading}
              >
                {isLoading ? 'Guardando...' : isEditing ? 'Actualizar' : 'Crear Servicio'}
              </Button>
            </div>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {confirmation}
    </>
  )
}
